import api from './api';

// TECNICA: capacitadores técnicos, a operarios / supervisores.
// SEGURIDAD: admins de Seguridad (SSHO), a cualquier funcionario activo.
export type TipoCapacitacion = 'TECNICA' | 'SEGURIDAD';

export const TIPOS_CAPACITACION: { value: TipoCapacitacion; label: string }[] = [
  { value: 'TECNICA', label: 'Técnica' },
  { value: 'SEGURIDAD', label: 'Seguridad' },
];

export const etiquetaTipo = (tipo?: string) =>
  TIPOS_CAPACITACION.find((t) => t.value === tipo)?.label || '';

export interface Tema {
  Id: number;
  Categoria: string;
  Tema: string;
  Horas: number;
  Participantes: string;
  Frecuencia: string;
  Tipo?: TipoCapacitacion;
  Activo?: boolean;
}

export interface RolCapacitacion {
  tipos_registro: TipoCapacitacion[];
  tipos_admin: TipoCapacitacion[];
}

export interface Funcionario {
  cedula: string;
  legajo: number | null;
  nombre: string;
  cargo: string;
  departamento: string;
}

export interface RegistroMultiplePayload {
  Tipo: TipoCapacitacion;
  FechaCapa: string;
  Legajo: number | string;
  TemaIds: number[];
  NombreUbicacion: string;
  PsCodigo?: string;
  Comentario?: string;
}

export interface Opcion {
  Id: number;
  Opcion: string;
}

export interface Pregunta {
  Id: number;
  Pregunta: string;
  Opciones: Opcion[];
}

export interface RespuestaPayload {
  PreguntaId: number;
  OpcionId: number;
}

export interface EvaluacionPayload {
  CapacitacionId: number;
  Respuestas: RespuestaPayload[];
}

export interface ResultadoEvaluacion {
  Puntaje: number;
  Correctas: number;
  Total: number;
  Detalle: { PreguntaId: number; Pregunta: string; Respuesta: string; Correcta: string | null; EsCorrecta: boolean }[];
}

// ── Mantenimiento (admins por tipo) ─────────────────────────────────────────
export interface TemaAdmin extends Tema {
  Activo: boolean;
  Preguntas: number;
  Registros: number;
}

export interface TemaPayload {
  Tipo?: TipoCapacitacion;
  Categoria: string;
  Tema: string;
  Horas: number | string;
  Participantes: string;
  Frecuencia: string;
}

export interface OpcionAdmin {
  Id?: number;
  Opcion: string;
  RespuestaCorrecta: boolean;
}

export interface PreguntaAdmin {
  Id?: number;
  Pregunta: string;
  Respondida?: boolean;
  Opciones: OpcionAdmin[];
}

// Se limpia en el logout (App.tsx removerSesion).
export const ROL_SESSION_KEY = 'capacitacion_rol_session';
const ROL_VACIO: RolCapacitacion = { tipos_registro: [], tipos_admin: [] };

const RegistroCapacitacionAPI = {
  /** Qué tipos puede registrar / administrar el usuario (cacheado en la sesión). */
  async miRol(forzar = false): Promise<RolCapacitacion> {
    if (!forzar) {
      try {
        const cache = sessionStorage.getItem(ROL_SESSION_KEY);
        if (cache) return JSON.parse(cache);
      } catch {
        // sin cache
      }
    }
    try {
      const res = await api.get('capacitacion/mi-rol');
      const rol: RolCapacitacion = { ...ROL_VACIO, ...(res.data?.datos || {}) };
      try { sessionStorage.setItem(ROL_SESSION_KEY, JSON.stringify(rol)); } catch { /* ignore */ }
      return rol;
    } catch {
      return ROL_VACIO;
    }
  },
  listarTemas(tipo: TipoCapacitacion = 'TECNICA') {
    return api.get('capacitacion/temas', { params: { tipo } });
  },
  async buscarFuncionarios(q: string): Promise<Funcionario[]> {
    const res = await api.get('capacitacion/funcionarios', { params: { q } });
    return res.data?.datos || [];
  },
  registrarMultiple(payload: RegistroMultiplePayload) {
    return api.post('capacitacion/registro-multiple', payload);
  },
  historial(legajo: number | string) {
    return api.get('capacitacion/historial', { params: { legajo } });
  },
  historialRecientes() {
    return api.get('capacitacion/historial/recientes');
  },
  listarPendientes() {
    return api.get('capacitacion/evaluacion/pendientes');
  },
  listarPreguntas(temaId: number) {
    // correccion=servidor: no recibir la opción correcta (la corrige el backend al guardar).
    return api.get('capacitacion/evaluacion/preguntas', { params: { tema_id: temaId, correccion: 'servidor' } });
  },
  guardarEvaluacion(payload: EvaluacionPayload) {
    return api.post('capacitacion/evaluacion', payload);
  },

  // Mantenimiento
  async adminTemas(tipo: TipoCapacitacion): Promise<TemaAdmin[]> {
    const res = await api.get('capacitacion/admin/temas', { params: { tipo } });
    return res.data?.datos || [];
  },
  adminCrearTema(payload: TemaPayload) {
    return api.post('capacitacion/admin/temas', payload);
  },
  adminActualizarTema(id: number, payload: TemaPayload) {
    return api.put(`capacitacion/admin/temas/${id}`, payload);
  },
  adminEstadoTema(id: number, activo: boolean) {
    return api.put(`capacitacion/admin/temas/${id}/estado`, { Activo: activo });
  },
  async adminPreguntas(id: number): Promise<{ Tema: Tema; Preguntas: PreguntaAdmin[] }> {
    const res = await api.get(`capacitacion/admin/temas/${id}/preguntas`);
    return res.data?.datos;
  },
  adminGuardarPreguntas(id: number, preguntas: PreguntaAdmin[]) {
    return api.put(`capacitacion/admin/temas/${id}/preguntas`, { Preguntas: preguntas });
  },
};

export default RegistroCapacitacionAPI;
